import { Chip } from '@emailforge/ui';

import type { DailyBucket, ProviderHealthRow } from '@/features/analytics/analyticsApi';
import { useListProvidersQuery } from '@/features/providers/providersApi';
import { formatNumber, formatPercent } from '@/lib/i18n';

type Props = {
  primary: ProviderHealthRow | undefined;
  byDay: DailyBucket[];
};

export function ProviderSnapshotCard({ primary, byDay }: Props) {
  const { data: providers = [] } = useListProvidersQuery();
  const provider = providers.find((p) => p.id === primary?.providerConnectionId);
  const last30 = byDay.slice(-30);
  const max = Math.max(...last30.map((d) => d.delivered + d.bounced), 1);

  const successRate = primary ? primary.successRate : null;
  const p50 = primary?.p50LatencyMs ?? null;
  const bounceRate = primary && primary.total > 0 ? primary.failed / primary.total : null;

  return (
    <div className="mf-card" style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {provider ? (
          <Chip variant="live">Connected</Chip>
        ) : (
          <Chip>No provider</Chip>
        )}
        <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          {provider ? `${provider.providerType} · ${provider.displayName}` : 'Connect a provider in Settings'}
        </span>
      </div>

      <div
        className="mono"
        style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.08, textTransform: 'uppercase' }}
      >
        Provider health · 30d
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(30, 1fr)',
          gap: 2,
          height: 40,
          alignItems: 'end',
        }}
      >
        {last30.length === 0
          ? Array.from({ length: 30 }).map((_, i) => (
              <div
                key={i}
                style={{ height: 4, background: 'var(--paper-4)', borderRadius: 1, opacity: 0.6 }}
              />
            ))
          : last30.map((d) => {
              const total = d.delivered + d.bounced;
              const h = Math.max(2, (total / max) * 38);
              const errored = d.bounced > 0 && d.bounced / Math.max(total, 1) > 0.05;
              return (
                <div
                  key={d.date}
                  title={`${d.date}: ${d.delivered} delivered · ${d.bounced} bounced`}
                  style={{
                    height: `${h}px`,
                    background: errored ? 'var(--err)' : 'var(--flame)',
                    borderRadius: 1,
                    opacity: errored ? 1 : 0.85,
                  }}
                />
              );
            })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <Stat
          value={successRate != null ? formatPercent(successRate, 2) : '—'}
          label="success · 30d"
        />
        <Stat value={p50 != null ? `${formatNumber(Math.round(p50))}ms` : '—'} label="p50 latency" />
        <Stat
          value={bounceRate != null ? formatPercent(bounceRate, 2) : '—'}
          label="bounce"
          tone={bounceRate != null && bounceRate > 0.02 ? 'warn' : undefined}
        />
      </div>
    </div>
  );
}

function Stat({ value, label, tone }: { value: string; label: string; tone?: 'warn' }) {
  return (
    <div>
      <div
        className="mono"
        style={{
          fontSize: 20,
          fontWeight: 500,
          color: tone === 'warn' ? 'var(--err)' : 'var(--ink)',
        }}
      >
        {value}
      </div>
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
        {label}
      </div>
    </div>
  );
}
